import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAdminRole } from '@/hooks/useAdminRole';

interface NavItem {
  key: string;
  label: string;
  icon: string;
  path: string;
  superAdminOnly?: boolean;
  children?: { key: string; label: string; path: string }[];
}

const navItems: NavItem[] = [
  {
    key: 'dashboard',
    label: '态势感知',
    icon: 'ri-dashboard-3-line',
    path: '/admin',
  },
  {
    key: 'cms',
    label: 'CMS内容管理',
    icon: 'ri-file-list-3-line',
    path: '/admin/cms',
    children: [
      { key: 'cms-hero', label: '首页轮播', path: '/admin/cms/hero' },
      { key: 'cms-business', label: '核心业务', path: '/admin/cms/business' },
      { key: 'cms-ai', label: 'AI产品', path: '/admin/cms/ai-products' },
      { key: 'cms-cases', label: '客户案例', path: '/admin/cms/cases' },
      { key: 'cms-news', label: '新闻动态', path: '/admin/cms/news' },
    ],
  },
  {
    key: 'users',
    label: '用户管理',
    icon: 'ri-team-line',
    path: '/admin/users',
    superAdminOnly: true,
  },
  {
    key: 'logs',
    label: '操作日志',
    icon: 'ri-file-history-line',
    path: '/admin/logs',
    superAdminOnly: true,
  },
];

interface AdminSidebarProps {
  collapsed: boolean;
}

export default function AdminSidebar({ collapsed }: AdminSidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isSuperAdmin } = useAdminRole();
  const [expandedKeys, setExpandedKeys] = useState<string[]>(['cms']);

  const visibleItems = navItems.filter(
    (item) => !item.superAdminOnly || isSuperAdmin
  );

  const toggleExpand = (key: string) => {
    setExpandedKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const isActive = (path: string) => location.pathname === path;
  const isParentActive = (item: NavItem) =>
    item.children?.some((c) => location.pathname === c.path) || location.pathname === item.path;

  return (
    <aside
      className={`fixed left-0 top-0 h-full bg-gray-900 text-white flex flex-col transition-all duration-300 z-40 ${
        collapsed ? 'w-16' : 'w-56'
      }`}
    >
      {/* Logo */}
      <div className="h-16 flex items-center px-4 border-b border-white/10 flex-shrink-0">
        {collapsed ? (
          <div className="w-8 h-8 flex items-center justify-center mx-auto">
            <i className="ri-shield-star-line text-teal-400 text-xl"></i>
          </div>
        ) : (
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 flex items-center justify-center bg-teal-500/20 rounded-lg">
              <i className="ri-shield-star-line text-teal-400 text-lg"></i>
            </div>
            <div>
              <div className="text-sm font-bold text-white leading-tight">龙创未来</div>
              <div className="text-xs text-gray-400 leading-tight">管理后台</div>
            </div>
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 px-2">
        {visibleItems.map((item) => (
          <div key={item.key} className="mb-1">
            <button
              onClick={() => {
                if (item.children) {
                  if (!collapsed) toggleExpand(item.key);
                } else {
                  navigate(item.path);
                }
              }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 cursor-pointer text-left ${
                isParentActive(item) && !item.children
                  ? 'bg-teal-600 text-white'
                  : isParentActive(item)
                  ? 'bg-white/10 text-white'
                  : 'text-gray-400 hover:bg-white/5 hover:text-white'
              }`}
            >
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i className={`${item.icon} text-lg`}></i>
              </div>
              {!collapsed && (
                <>
                  <span className="flex-1 text-sm font-medium whitespace-nowrap">{item.label}</span>
                  {item.children && (
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i
                        className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${
                          expandedKeys.includes(item.key) ? 'rotate-180' : ''
                        }`}
                      ></i>
                    </div>
                  )}
                </>
              )}
            </button>

            {/* Children */}
            {item.children && !collapsed && expandedKeys.includes(item.key) && (
              <div className="mt-1 ml-4 pl-3 border-l border-white/10 space-y-0.5">
                {item.children.map((child) => (
                  <button
                    key={child.key}
                    onClick={() => navigate(child.path)}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all duration-200 cursor-pointer text-left ${
                      isActive(child.path)
                        ? 'bg-teal-600 text-white'
                        : 'text-gray-400 hover:bg-white/5 hover:text-white'
                    }`}
                  >
                    <div className="w-1.5 h-1.5 rounded-full bg-current flex-shrink-0"></div>
                    <span className="whitespace-nowrap">{child.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </nav>

      {/* Back to site */}
      <div className="p-3 border-t border-white/10 flex-shrink-0">
        <button
          onClick={() => navigate('/')}
          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-400 hover:bg-white/5 hover:text-white transition-all duration-200 cursor-pointer ${
            collapsed ? 'justify-center' : ''
          }`}
        >
          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
            <i className="ri-arrow-left-line text-lg"></i>
          </div>
          {!collapsed && <span className="text-sm whitespace-nowrap">返回官网</span>}
        </button>
      </div>
    </aside>
  );
}
